import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ClipboardList,
  Clock,
  AlertTriangle,
  FileText,
  Link as LinkIcon,
  ExternalLink,
  Send,
  Download,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OrderTrackingPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("md_orders");
  const [updatingStatus, setUpdatingStatus] = useState(null);

  const MD_ORDER_STATUSES = [
    { value: "pending", label: "Pending" },
    { value: "sent", label: "Sent" },
    { value: "signed", label: "Signed" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
  ];

  const updateMdOrderStatus = async (orderId, newStatus) => {
    setUpdatingStatus(orderId);
    try {
      await axios.patch(`${API}/md-orders/${orderId}`, { status: newStatus });
      toast.success(`Order status set to ${newStatus}`);
      fetchOrderTracking();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    } finally {
      setUpdatingStatus(null);
    }
  };

  useEffect(() => {
    fetchOrderTracking();
  }, []);

  const fetchOrderTracking = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/order-tracking`);
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch order tracking:", error);
      toast.error("Failed to load order tracking data");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard!");
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center text-slate-500">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
            <p>Loading order tracking...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const summary = data?.summary || {};

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Order Tracking</h1>
            <p className="text-slate-500">Monitor pending orders and signature requests across all clients</p>
          </div>
          <Button variant="outline" onClick={fetchOrderTracking} className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-5 gap-4">
          <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <ClipboardList className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{summary.total_pending_orders || 0}</p>
                <p className="text-xs text-slate-500">Pending MD Orders</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <LinkIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{summary.total_pending_sign_links || 0}</p>
                <p className="text-xs text-slate-500">Pending Sign Links</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{summary.total_overdue || 0}</p>
                <p className="text-xs text-slate-500">Overdue/Expiring</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{summary.total_unsigned_cms485 || 0}</p>
                <p className="text-xs text-slate-500">Unsigned CMS 485</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-100 rounded-lg">
                <ClipboardList className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{summary.total_unsigned_care_plans || 0}</p>
                <p className="text-xs text-slate-500">Unsigned Care Plans</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full justify-start border-b border-slate-200 rounded-none bg-transparent p-0">
              <TabsTrigger value="md_orders" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none">
                MD Orders ({data?.pending_md_orders?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="sign_links" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none">
                Sign Links ({data?.pending_sign_links?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="overdue" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none">
                <AlertTriangle className="w-4 h-4 mr-1 text-red-500" />
                Overdue ({data?.overdue_sign_links?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="cms485" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none">
                CMS 485 ({data?.unsigned_cms485?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="care_plans" className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none">
                Care Plans ({data?.unsigned_care_plans?.length || 0})
              </TabsTrigger>
            </TabsList>

            {/* MD Orders Tab */}
            <TabsContent value="md_orders" className="p-4">
              {data?.pending_md_orders?.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No pending MD orders</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Order Types</TableHead>
                      <TableHead>Physician</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.pending_md_orders?.map((order) => (
                      <TableRow key={order.id}>
                        <TableCell className="font-medium">
                          <Link to={`/clients/${order.client_id}`} className="text-primary hover:underline">
                            {order.client_name || "Unknown"}
                          </Link>
                        </TableCell>
                        <TableCell className="capitalize">
                          {order.order_types?.map(t => t.replace(/_/g, ' ')).join(', ')}
                        </TableCell>
                        <TableCell>{order.physician_name || "-"}</TableCell>
                        <TableCell>
                          <Select
                            value={order.status}
                            onValueChange={(v) => updateMdOrderStatus(order.id, v)}
                            disabled={updatingStatus === order.id}
                          >
                            <SelectTrigger
                              className={`h-7 w-[130px] text-xs font-medium ${
                                order.status === 'completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                order.status === 'signed' ? 'bg-teal-50 text-teal-700 border-teal-200' :
                                order.status === 'sent' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                order.status === 'cancelled' ? 'bg-slate-50 text-slate-500 border-slate-200' :
                                'bg-amber-50 text-amber-700 border-amber-200'
                              }`}
                              data-testid={`md-order-status-${order.id}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {MD_ORDER_STATUSES.map((s) => (
                                <SelectItem key={s.value} value={s.value}>
                                  {s.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm">
                          {order.created_at?.split('T')[0]}
                        </TableCell>
                        <TableCell className="text-right">
                          <Link to={`/clients/${order.client_id}`}>
                            <Button variant="ghost" size="sm">
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            {/* Sign Links Tab */}
            <TabsContent value="sign_links" className="p-4">
              {data?.pending_sign_links?.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <LinkIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No pending sign links</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Document Type</TableHead>
                      <TableHead>Physician</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.pending_sign_links?.map((link) => (
                      <TableRow key={link.id}>
                        <TableCell className="font-medium">{link.client_name}</TableCell>
                        <TableCell className="capitalize">{link.document_type?.replace(/_/g, ' ')}</TableCell>
                        <TableCell>{link.physician_name}</TableCell>
                        <TableCell>
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                            link.status === 'viewed' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {link.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm">
                          {link.expires_at?.split('T')[0]}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyLink(link.secure_url)}
                            title="Copy link"
                          >
                            <LinkIcon className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            {/* Overdue Tab */}
            <TabsContent value="overdue" className="p-4">
              {data?.overdue_sign_links?.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No overdue or expiring sign links</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Document Type</TableHead>
                      <TableHead>Physician</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Expires In</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.overdue_sign_links?.map((link) => (
                      <TableRow key={link.id} className={link.is_overdue ? "bg-red-50" : "bg-amber-50"}>
                        <TableCell className="font-medium">{link.client_name}</TableCell>
                        <TableCell className="capitalize">{link.document_type?.replace(/_/g, ' ')}</TableCell>
                        <TableCell>{link.physician_name}</TableCell>
                        <TableCell>
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                            link.is_overdue ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {link.is_overdue ? 'EXPIRED' : 'Expiring Soon'}
                          </span>
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm">
                          {link.is_overdue ? 'Expired' : `${link.days_until_expiry} days`}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyLink(link.secure_url)}
                            title="Copy link to resend"
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            {/* CMS 485 Tab */}
            <TabsContent value="cms485" className="p-4">
              {data?.unsigned_cms485?.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>All CMS 485 forms are signed</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Certification Period</TableHead>
                      <TableHead>Physician</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.unsigned_cms485?.map((form) => (
                      <TableRow key={form.id}>
                        <TableCell className="font-medium">
                          <Link to={`/clients/${form.client_id}`} className="text-primary hover:underline">
                            {form.client_name || "Unknown"}
                          </Link>
                        </TableCell>
                        <TableCell>
                          {form.certification_period_from} - {form.certification_period_to}
                        </TableCell>
                        <TableCell>{form.physician_name || "-"}</TableCell>
                        <TableCell className="text-slate-500 text-sm">
                          {form.created_at?.split('T')[0]}
                        </TableCell>
                        <TableCell className="text-right">
                          <Link to={`/clients/${form.client_id}`}>
                            <Button variant="ghost" size="sm">
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>

            {/* Care Plans Tab */}
            <TabsContent value="care_plans" className="p-4">
              {data?.unsigned_care_plans?.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>All care plans are fully signed</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>SOC Date</TableHead>
                      <TableHead>Hours/Week</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.unsigned_care_plans?.map((poc) => (
                      <TableRow key={poc.id}>
                        <TableCell className="font-medium">
                          <Link to={`/clients/${poc.client_id}`} className="text-primary hover:underline">
                            {poc.client_name || "Unknown"}
                          </Link>
                        </TableCell>
                        <TableCell>{poc.soc_date || "-"}</TableCell>
                        <TableCell>{poc.hours_per_week || "-"}</TableCell>
                        <TableCell>
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                            poc.status === 'pending_signatures' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {poc.status?.replace(/_/g, ' ')}
                          </span>
                        </TableCell>
                        <TableCell className="text-slate-500 text-sm">
                          {poc.created_at?.split('T')[0]}
                        </TableCell>
                        <TableCell className="text-right">
                          <Link to={`/clients/${poc.client_id}`}>
                            <Button variant="ghost" size="sm">
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Layout>
  );
}
